import { useTranslation } from '@govard/i18n-core/react';
import type { ReactElement } from 'react';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import { formatDate, formatDateTime, formatMoney } from '../../../../shared/format/index.js';
import { toInAppCrmHref } from '../../lib/crmInAppHref.js';

export type CrmListPayload = Extract<StructuredMessagePayload, { type: 'crm_list_v1' }>;

/** Date-only and datetime scalars as adapters emit them. */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/u;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/u;

/**
 * Enum columns whose label must not break inside a word when the table gets
 * narrow ("К выполнени / ю"); the title column never wraps because it has its
 * own max width.
 */
const NOWRAP_CELL_KEYS = new Set(['status', 'priority', 'stage']);

interface CrmStructuredListProps {
  readonly payload: CrmListPayload;
  readonly compact?: boolean | undefined;
  readonly onNextPage?: (() => void) | undefined;
}

function entityLabelKey(entityType: string): string {
  return `crm.chat.crm_list.entity.${entityType}`;
}

/**
 * A stage value (or a comma-joined stage filter) localized through the same
 * catalog the deals pages use; null when a part is not a known stage, so an
 * unknown value is shown as stored rather than half-translated.
 */
function localizedStage(value: string, t: ReturnType<typeof useTranslation>['t']): string | null {
  const parts = value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (parts.length === 0) return null;
  const labels = parts.map((part) => {
    const key = `crm.deals.stage.${part}`;
    const translated = t(key);
    return translated === key ? null : translated;
  });
  return labels.every((label) => label !== null) ? labels.join(', ') : null;
}

function fieldValue(
  value: string | number | boolean | null,
  key?: string,
  t?: ReturnType<typeof useTranslation>['t'],
  entityType?: string,
): string {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? '✓' : '—';
  if (typeof value === 'string' && t !== undefined && key === 'stage') {
    const localized = localizedStage(value, t);
    if (localized !== null) return localized;
  }
  if (entityType === 'task' && typeof value === 'string' && t !== undefined) {
    const labelKey =
      key === 'status'
        ? `crm.dashboard.status.${value}`
        : key === 'priority'
          ? `crm.chat.action_plan.priority.${value}`
          : undefined;
    if (labelKey !== undefined) {
      const translated = t(labelKey);
      if (translated !== labelKey) return translated;
    }
  }
  // A timestamp column renders exactly like the Tasks page: the shared
  // frontend formatter (dd.MM.yyyy, HH:mm in the locale), never raw ISO.
  if (typeof value === 'string' && ISO_DATE_TIME.test(value)) return formatDateTime(value);
  if (typeof value === 'string' && ISO_DATE_ONLY.test(value)) return formatDate(value);
  return String(value);
}

/**
 * A row cell. The amount carries its own currency (the deals-page formatter),
 * so the separate currency column is dropped and the number is grouped.
 */
function cellValue(
  valuesByKey: ReadonlyMap<string, string | number | boolean | null>,
  key: string,
  t: ReturnType<typeof useTranslation>['t'],
  entityType: string,
): string {
  const value = valuesByKey.get(key) ?? null;
  if (key === 'amount' && value !== null) {
    const currency = valuesByKey.get('currency');
    return formatMoney(
      typeof value === 'string' ? value : Number(value),
      typeof currency === 'string' ? currency : undefined,
    );
  }
  return fieldValue(value, key, t, entityType);
}

/** Relation filter keys (`company.companyId`) mapped onto the plain field labels. */
const RELATION_FIELD_KEYS: Readonly<Record<string, string>> = {
  companyId: 'company',
  contactId: 'contact',
  dealId: 'deal',
  projectId: 'project',
  assigneeMemberId: 'assignee',
  documentId: 'document',
};

function localizedFieldLabel(
  t: ReturnType<typeof useTranslation>['t'],
  key: string,
  fallback: string,
): string {
  // A relation filter ships as a dotted `entity.field` key. A raw dotted key
  // must never reach the chip: known relations map onto their field label, any
  // other relation degrades to a generic "Filter" label.
  const relationKey = key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : undefined;
  const fieldKey = relationKey === undefined ? key : RELATION_FIELD_KEYS[relationKey];
  if (fieldKey !== undefined) {
    const translated = t(`crm.chat.crm_list.field.${fieldKey}`);
    if (translated !== `crm.chat.crm_list.field.${fieldKey}`) return translated;
  }
  if (relationKey !== undefined) return t('crm.chat.crm_list.filter_label');
  return fallback;
}

/**
 * One applied-filter chip. The operator is folded into the template rather than
 * printed: "equals" adds nothing before the value, "contains" reads as a quoted
 * term, and any operator we have no template for degrades to the neutral
 * `field: value` form — a raw token like `gte` must never reach the reader.
 */
function filterChipLabel(
  t: ReturnType<typeof useTranslation>['t'],
  filter: {
    readonly field: string;
    readonly operator: string;
    readonly value: string | number | boolean | null;
  },
  entityType: string,
): string {
  const field = localizedFieldLabel(t, filter.field, filter.field);
  // A due window travels as its server keyword and gets a localized day label.
  // The operator differs by window (`lte` day plan / `between` week) and never
  // participates, so it is not looked up as a template.
  if (
    filter.field === 'dueAt' &&
    (filter.value === 'today' || filter.value === 'week' || filter.value === 'tomorrow')
  ) {
    return t('crm.chat.crm_list.filter_between', {
      field,
      value: t(`crm.chat.crm_list.window.${filter.value}`),
    });
  }
  // An amount keeps the same grouping + currency the column shows.
  const value =
    filter.field === 'amount' && filter.value !== null
      ? formatMoney(typeof filter.value === 'string' ? filter.value : Number(filter.value))
      : fieldValue(filter.value, filter.field, t, entityType);
  const operatorKey = `crm.chat.crm_list.filter_${filter.operator}`;
  const template = t(operatorKey, { field, value });
  return template === operatorKey
    ? t('crm.chat.crm_list.filter_equals', { field, value })
    : template;
}

export function CrmStructuredList({
  payload,
  compact = false,
  onNextPage,
}: CrmStructuredListProps): ReactElement {
  const { t } = useTranslation();
  const { list } = payload;
  const entityKey = entityLabelKey(list.entityType);
  const entityTitle =
    t(entityKey) === entityKey ? t('crm.chat.crm_list.entity.default') : t(entityKey);
  // Column headers come from the ordered union of field keys, not per-row:
  // flat-mapping every row would repeat the same header once per row, and rows
  // may legitimately carry different field sets.
  const fieldKeys: string[] = [];
  const fieldLabels = new Map<string, string>();
  for (const row of list.rows) {
    for (const field of row.fields) {
      // The currency rides with the amount cell, so it gets no column of its own.
      if (field.key === 'currency') continue;
      if (!fieldLabels.has(field.key)) {
        fieldLabels.set(field.key, localizedFieldLabel(t, field.key, field.label));
        fieldKeys.push(field.key);
      }
    }
  }
  const rowsWithValues = list.rows.map((row) => ({
    row,
    valuesByKey: new Map(row.fields.map((field) => [field.key, field.value])),
  }));
  // A column that is empty for every row on the current page is noise: the
  // task card showed ten mostly-"—" columns and the status label broke
  // mid-word. Hidden for every entity card; the title column is structural
  // and never hidden.
  const visibleFieldKeys = fieldKeys.filter((key) =>
    rowsWithValues.some(({ valuesByKey }) => {
      const display = cellValue(valuesByKey, key, t, list.entityType);
      return display !== '—' && display.trim() !== '';
    }),
  );
  const columns = [
    t('crm.chat.crm_list.column.name'),
    ...visibleFieldKeys.map((key) => fieldLabels.get(key) ?? key),
  ];

  return (
    <section className="min-w-0 overflow-hidden rounded-lg border border-amber-200/80 bg-white">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-100 bg-amber-50/50 px-3 py-2.5">
        <div className="min-w-0">
          <h3 className="m-0 truncate text-sm font-semibold text-amber-950">
            {t('crm.chat.crm_list.title', { entity: entityTitle })}
          </h3>
          <p className="mt-0.5 text-xs text-amber-800">
            {t('crm.chat.crm_list.total', { count: list.total })}
          </p>
        </div>
        {list.hasMore ? (
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-900">
              {t('crm.chat.crm_list.more_available')}
            </span>
            {onNextPage !== undefined ? (
              <button
                type="button"
                className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50"
                onClick={onNextPage}
              >
                {t('crm.chat.crm_list.next_page')}
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {list.appliedFilters.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 border-b border-stone-100 px-3 py-2">
          {list.appliedFilters.map((filter) => (
            <span
              key={`${filter.field}:${filter.operator}:${String(filter.value)}`}
              className="rounded-md bg-stone-100 px-2 py-1 text-[11px] text-stone-700"
            >
              {filterChipLabel(t, filter, list.entityType)}
            </span>
          ))}
        </div>
      ) : null}

      {list.rows.length === 0 ? (
        <p className="px-3 py-4 text-sm text-stone-500">{t('crm.chat.crm_list.empty')}</p>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse text-left text-xs">
            <thead className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-500">
              <tr>
                {columns.map((column) => (
                  <th key={column} className="whitespace-nowrap px-3 py-2 font-medium">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowsWithValues.map(({ row, valuesByKey }) => (
                <tr key={row.id} className="border-t border-stone-100 hover:bg-amber-50/30">
                  <td className="max-w-[260px] px-3 py-2 font-medium text-stone-800">
                    {row.url === null ? (
                      row.label
                    ) : (
                      <a
                        href={toInAppCrmHref(row.url)}
                        className="text-amber-800 underline decoration-amber-200 underline-offset-2 hover:text-amber-950"
                      >
                        {row.label}
                      </a>
                    )}
                  </td>
                  {visibleFieldKeys.map((key) => (
                    <td
                      key={`${row.id}:${key}`}
                      className={`max-w-[220px] px-3 py-2 text-stone-600${
                        NOWRAP_CELL_KEYS.has(key) ? ' whitespace-nowrap' : ''
                      }`}
                    >
                      {cellValue(valuesByKey, key, t, list.entityType)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!compact && list.rows.length > 0 ? (
        <p className="border-t border-stone-100 px-3 py-2 text-[11px] text-stone-500">
          {t('crm.chat.crm_list.source')}
        </p>
      ) : null}
    </section>
  );
}
